import React, { useMemo, useState, useEffect } from "react";
import { useCandleData } from "../context/MarketContext";
import { useTheme } from "../context/ThemeContext.js";

const Candle = ({
  wickHeight,
  bodyHeight,
  bodyOffset,
  containerBottom,
  type,
  label,
  isLive,
  price,
  progress,
  closePixelOffset,
  isMobile,
  isLight,
}) => {
  const isBull = type === "bull";

  return (
    <div
      className={`absolute preserve-3d transition-all ${
        isLive ? "z-20" : "z-10"
      }`}
      style={{
        width: isMobile ? "32px" : "40px",
        bottom: `${containerBottom}%`,
        left: "50%",
        marginLeft: isMobile ? "-16px" : "-20px",
        transition: isLive
          ? "all 0.3s ease-out"
          : "all 1.2s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      {/* Wick */}
      <div
        className={`absolute left-1/2 -translate-x-1/2 w-[1px] sm:w-[1.5px] ${
          isLive
            ? isLight ? "bg-primary/40 shadow-[0_0_8px_#5e19e6]" : "bg-accent/40 shadow-[0_0_8px_#00f0ff]"
            : isLight ? "bg-gray-400/30" : "bg-white/20"
        }`}
        style={{
          height: `${wickHeight}px`,
          bottom: 0,
          transition: isLive ? "height 0.3s ease-out" : "height 0.7s ease-out",
        }}
      />

      {/* Body */}
      <div
        className={`candle-body absolute rounded-sm ${
          isBull
            ? "bg-gradient-to-b from-white to-primary shadow-[0_0_25px_rgba(94,25,230,0.4)]"
            : isLight
              ? "bg-gradient-to-b from-gray-500 to-gray-400 border border-gray-400 backdrop-blur-sm"
              : "bg-gradient-to-b from-white/40 to-white/5 border border-white/20 backdrop-blur-sm"
        } ${isLive ? (isLight ? "ring-1 ring-primary/30 animate-pulse-slow" : "ring-1 ring-accent/30 animate-pulse-slow") : ""}`}
        style={{
          width: isMobile ? "16px" : "24px",
          left: isMobile ? "8px" : "8px",
          height: `${Math.max(bodyHeight, 2)}px`,
          bottom: `${bodyOffset}px`,
          boxSizing: "border-box",
          transition: isLive ? "all 0.3s ease-out" : "all 1s ease-in-out",
        }}
      />

      {/* Live Candle Progress Bar */}
      {isLive && progress !== undefined && (
        <div className="absolute -bottom-6 sm:-bottom-8 left-0 w-full">
          <div className={`w-full h-0.5 sm:h-1 rounded-full overflow-hidden ${isLight ? 'bg-gray-300' : 'bg-white/10'}`}>
            <div
              className={`h-full bg-gradient-to-r transition-all duration-500 ${isLight ? 'from-primary to-primary/60' : 'from-accent to-primary'}`}
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className={`text-[6px] sm:text-[7px] font-mono text-center mt-0.5 sm:mt-1 ${isLight ? 'text-primary/70' : 'text-accent/70'}`}>
            {progress}%
          </div>
        </div>
      )}

      {/* Label/Price Tag */}
      {(label || isLive) && (
        <div
          className={`absolute left-full ml-2 sm:ml-4 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded font-mono text-[7px] sm:text-[9px] whitespace-nowrap transition-all duration-300 ${
            isLive
              ? isLight
                ? "bg-primary text-white font-bold scale-100 sm:scale-110 shadow-[0_0_15px_#5e19e6]"
                : "bg-accent text-black font-bold scale-100 sm:scale-110 shadow-[0_0_15px_#00f0ff]"
              : isLight
                ? "bg-primary/20 text-gray-700 border border-primary/50"
                : "bg-primary/20 text-white border border-primary/50"
          }`}
          style={{
            bottom: `${closePixelOffset}px`,
            transform: "translateY(50%)",
          }}
        >
          {isLive && <span className="mr-1 sm:mr-2 animate-pulse">●</span>}
          {isMobile ? `$${price}` : (label || `$${price}`)}
        </div>
      )}
    </div>
  );
};

const Candlestick3D = () => {
  const { allCandles } = useCandleData();
  const { isLight } = useTheme();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const SCENE_HEIGHT = isMobile ? 200 : 280;

  const positions = useMemo(
    () => {
      const scale = isMobile ? 0.5 : 1;
      return [
        { x: -180 * scale, z: -40 * scale },
        { x: -90 * scale, z: 0 },
        { x: 0, z: 40 * scale },
        { x: 90 * scale, z: 80 * scale },
        { x: 180 * scale, z: 120 * scale },
      ];
    },
    [isMobile]
  );

  const visualsData = useMemo(() => {
    if (allCandles.length === 0) return [];

    const minPrice = Math.min(...allCandles.map((c) => c.low)) * 0.999;
    const maxPrice = Math.max(...allCandles.map((c) => c.high)) * 1.001;
    const priceRange = maxPrice - minPrice || 1;

    return allCandles.map((candle) => {
      const { open, high, low, close } = candle;

      const containerBottom = ((low - minPrice) / priceRange) * 100;
      const wickHeight = ((high - low) / priceRange) * SCENE_HEIGHT;

      const bodyBottomPrice = Math.min(open, close);
      const bodyTopPrice = Math.max(open, close);
      const bodyHeight =
        ((bodyTopPrice - bodyBottomPrice) / priceRange) * SCENE_HEIGHT;
      const bodyOffset = ((bodyBottomPrice - low) / priceRange) * SCENE_HEIGHT;

      const closePixelOffset = ((close - low) / priceRange) * SCENE_HEIGHT;

      return {
        wickHeight: Math.max(wickHeight, 4),
        bodyHeight: Math.max(bodyHeight, 2),
        bodyOffset,
        containerBottom,
        closePixelOffset,
        price: close.toFixed(2),
        type: candle.type,
        isLive: candle.closed === false,
        progress: candle.progress,
        id: candle.id,
      };
    });
  }, [allCandles, SCENE_HEIGHT]);

  if (allCandles.length === 0) return null;

  const latestVisual = visualsData[visualsData.length - 1];
  const livePos = positions[positions.length - 1];

  return (
    <div className="scene-3d w-full h-full flex items-center justify-center pointer-events-none p-4 sm:p-10 md:p-20">
      <div
        className="chart-grid-3d w-full relative preserve-3d"
        style={{ height: `${SCENE_HEIGHT}px` }}
      >
        {/* FLOOR GRID */}
        <div className={`absolute bottom-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent to-transparent ${isLight ? 'via-gray-400/20' : 'via-white/20'}`}></div>

        {/* SYNCED LIVE PRICE LINE */}
        <div
          className="absolute inset-0 preserve-3d transition-transform duration-300"
          style={{ transform: `translateZ(${livePos.z}px)` }}
        >
          {latestVisual && (
            <div
              className="absolute w-full"
              style={{
                bottom: `calc(${latestVisual.containerBottom}% + ${latestVisual.closePixelOffset}px)`,
              }}
            >
              {/* Horizontal line */}
              <div
                className={`absolute left-[-50%] sm:left-[-100%] right-[-50%] sm:right-[-100%] h-[1px] opacity-40 ${
                  isLight ? 'bg-primary/40 shadow-[0_0_15px_#5e19e6]' : 'bg-accent/40 shadow-[0_0_15px_#00f0ff]'
                }`}
                style={{ transform: "translateY(0.5px)" }}
              />

              {/* Price Badge */}
              <div className="absolute right-0 -top-[9px] sm:-top-[11px] translate-x-full ml-1 sm:ml-2">
                <div className={`font-bold font-mono text-[8px] sm:text-[10px] px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-sm whitespace-nowrap ${
                  isLight ? 'bg-primary text-white shadow-[0_0_15px_#5e19e6]' : 'bg-accent text-black shadow-[0_0_15px_#00f0ff]'
                }`}>
                  ${latestVisual.price}
                </div>
                <div className={`absolute top-1/2 -left-[3px] sm:-left-[4px] -translate-y-1/2 w-0 h-0 border-t-[3px] sm:border-t-[4px] border-t-transparent border-b-[3px] sm:border-b-[4px] border-b-transparent border-r-[3px] sm:border-r-[4px] ${
                  isLight ? 'border-r-primary' : 'border-r-accent'
                }`} />
              </div>
            </div>
          )}
        </div>

        {/* CANDLES GROUP */}
        <div className="relative w-full h-full preserve-3d">
          {visualsData.map((visual, index) => {
            const pos = positions[index];
            return (
              <div
                key={visual.id || index}
                className="absolute inset-0 preserve-3d transition-transform duration-1000"
                style={{
                  transform: `translateX(${pos.x}px) translateZ(${pos.z}px)`,
                }}
              >
                <Candle
                  {...visual}
                  isMobile={isMobile}
                  isLight={isLight}
                  label={visual.isLive ? (isMobile ? null : "SIMULATION") : (index === 2 && !isMobile ? "PIVOT" : null)}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Candlestick3D;
